import { Text, View } from "react-native";
import LoadingSpinner from "ui/atoms/spinner/LoadingSpinner";
import { Button } from "ui/gluestack/button";
import {
  Checkbox,
  CheckboxIcon,
  CheckboxIndicator,
  CheckboxLabel,
} from "ui/gluestack/checkbox";
import { AddIcon, CheckIcon, Icon } from "ui/gluestack/icon";
import { Controller } from "react-hook-form";
import { FormControlledDropdown } from "ui/molecules/form/FormControlledDropdown";
import { FormControlledInput } from "ui/molecules/form/FormControlledInput";
import {
  minLengthValidation,
  requiredValidation,
  validationSchema,
} from "ui/utils/Validators";
import { CreateRoleRequest } from "app/services/api/types/roles/requests/CreateRoleRequest";
import { useHandleRoleForm } from "../hooks/useHandleRoleForm";
import { useHandleRoleListing } from "../hooks/useHandleRoleListing";
import { RoleModel } from "app/services/api/types/roles/models/RoleModel";

interface Props {
  onClose: () => void;
  onSuccess?: () => void;
  isEditing?: boolean;
  initialRole?: RoleModel;
  currentTitle: string;
}

export default function AddRoleForm({
  onClose,
  onSuccess,
  isEditing,
  initialRole,
  currentTitle,
}: Props) {
  const {
    arePermissionsLoading,
    areRolesLoading,
    rolesOptions,
    sortedPermissionsData,
    handleCheckboxSelection,
  } = useHandleRoleListing();

  const { control, submitForm, isLoadingAction } = useHandleRoleForm(
    onSuccess,
    initialRole,
    currentTitle,
  );

  if (arePermissionsLoading || areRolesLoading) {
    return (
      <View className="flex-1 justify-center items-center my-10">
        <LoadingSpinner />
      </View>
    );
  }

  return (
    <View>
      <View className="grid sm:grid-cols-2 sm:gap-x-6 gap-y-6 sm:gap-y-0">
        {isEditing ? null : (
          <FormControlledInput<CreateRoleRequest>
            id="display_name"
            name="display_name"
            type="text"
            control={control}
            label={"Role name"}
            placeholder={"Enter role"}
            isRequired
            isDisabled={isLoadingAction}
            rules={{
              minLength: minLengthValidation(validationSchema.name.minLength),
              required: requiredValidation("Role name"),
            }}
            onSubmitEditing={submitForm}
          />
        )}
        <FormControlledDropdown<CreateRoleRequest>
          name="parent_id"
          control={control}
          label="Role manager"
          placeholder={"Select role manager"}
          isRequired
          isDisabled={isLoadingAction}
          options={rolesOptions}
          rules={{
            required: requiredValidation("Role manager"),
          }}
        />
      </View>

      <View className="border border-gray-200 w-full my-6" />

      <Text className="text-base font-semibold text-typography-600 mb-6">
        Permissions
      </Text>
      {sortedPermissionsData.map((groupedPermission, index) => (
        <View key={groupedPermission.group}>
          <Text className="text-sm font-semibold text-typography-600">
            {groupedPermission.group}
          </Text>
          <View className="grid md:grid-cols-3 sm:grid-cols-2">
            {groupedPermission.permissions.map((permission) => (
              <Controller
                key={permission.id}
                control={control}
                name={"permissions"}
                defaultValue={[]}
                render={({ field: { value = [], onChange } }) => {
                  const isChecked = value.includes(permission.id);

                  const handleCheckboxChange = (checked: boolean) => {
                    const newPermissions = handleCheckboxSelection(
                      checked,
                      permission.id,
                      value,
                      groupedPermission.permissions,
                    );
                    onChange(newPermissions);
                  };

                  return (
                    <View className="flex-row items-center mt-5 min-w-0 pr-4">
                      <Checkbox
                        size="sm"
                        isChecked={isChecked}
                        onChange={handleCheckboxChange}
                        value={value ? "checked" : "unchecked"}
                        className="flex-1 min-w-0 items-start"
                      >
                        <CheckboxIndicator className="mt-0.5">
                          <CheckboxIcon as={CheckIcon} />
                        </CheckboxIndicator>
                        <CheckboxLabel className="ml-2 flex-1 flex-shrink">
                          {permission.display_name}
                        </CheckboxLabel>
                      </Checkbox>
                    </View>
                  );
                }}
              />
            ))}
          </View>
          {index < sortedPermissionsData.length - 1 && (
            <View className="border border-gray-200 w-full my-8" />
          )}
        </View>
      ))}

      <View className="flex flex-row justify-end gap-x-4 mt-10 pb-4">
        <Button action="primary" size="sm" variant="outline" onPress={onClose}>
          <Text className="text-primary-500 font-semibold">Cancel</Text>
        </Button>
        <Button action="primary" size="sm" onPress={submitForm}>
          {isEditing ? (
            <Text className="text-white">Save changes</Text>
          ) : (
            <>
              <Text className="text-white">Create role</Text>
              <Icon as={AddIcon} className="w-4 h-4 stroke-white" />
            </>
          )}
        </Button>
      </View>
    </View>
  );
}
